import React, { useEffect, useState } from 'react';
import {
    Button,
    Modal,
    Form,
    Input,
    Space,
    Tooltip,
    message,
    Spin,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import * as API from '../../../api/PharmacyNoteConfigsAPI';
import { usePharmacyMultiSelect } from '../../../hooks/usePharmacyMultiSelect';
import {
    FieldLabel,
    HelpText,
    ModalTitle,
    ModalFooter,
    SaveButton,
    PharmacyTag,
    PharmacyTagState,
    PharmacyTagId,
    PharmacyTagRemove,
    PharmacyTagList,
    FullWidthAutoComplete,
    MaxCharsInput,
    TooltipIcon,
    TooltipContent,
    TooltipTable,
    TooltipPlaceholderCell,
    TooltipDescCell,
    TooltipCode,
    TooltipExample,
    TooltipMutedText,
    TooltipStrongText,
} from './styles';

const { TextArea } = Input;

const DEFAULT_MAX_CHARACTERS = 210;

const PLACEHOLDER_DOCS = [
    { placeholder: '{{pharmacyNotes}}', description: 'The notes entered by the provider at time of prescribing' },
    { placeholder: '{{clientName}}', description: 'The client / brand name (e.g. "LifeMD")' },
    { placeholder: '{{date}}', description: 'Current date in MM/DD/YYYY format (UTC)' },
    { placeholder: '{{dateET}}', description: 'Current date in MM/DD/YYYY format (America/New_York) — use for pharmacies that require Eastern time' },
    { placeholder: '{{patientId}}', description: "The patient's internal MongoDB ID" },
    { placeholder: '{{daw}}', description: 'Resolves to "DAW, " when Dispense As Written is set, otherwise empty' },
];

const PlaceholderHelp = () => (
    <TooltipContent>
        <TooltipStrongText>Available template placeholders:</TooltipStrongText>
        <TooltipTable>
            <tbody>
                {PLACEHOLDER_DOCS.map(({ placeholder, description }) => (
                    <tr key={placeholder}>
                        <TooltipPlaceholderCell>
                            <TooltipCode>{placeholder}</TooltipCode>
                        </TooltipPlaceholderCell>
                        <TooltipDescCell>{description}</TooltipDescCell>
                    </tr>
                ))}
            </tbody>
        </TooltipTable>
        <TooltipExample>
            <TooltipMutedText>Example: </TooltipMutedText>
            <TooltipCode $small>{'{{pharmacyNotes}} - Brand: {{clientName}}, LTC {{date}}'}</TooltipCode>
        </TooltipExample>
    </TooltipContent>
);

export const ConfigModal = ({ open, onCancel, onSuccess, editingConfig }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const {
        selectedPharmacies,
        loadingPharmacies,
        searchValue,
        setSearchValue,
        pharmacyOptions,
        pharmaciesLoading,
        handleSearch,
        handleSelectPharmacy,
        handleRemovePharmacy,
        reset,
        hydrate,
    } = usePharmacyMultiSelect({
        onFirstSelect: (pharmacy) => {
            if (!form.getFieldValue('pharmacyName')) {
                form.setFieldsValue({ pharmacyName: pharmacy.name });
            }
        },
    });

    useEffect(() => {
        if (!open) return;

        if (editingConfig) {
            form.setFieldsValue({
                pharmacyName: editingConfig.pharmacyName,
                noteTemplate: editingConfig.noteTemplate,
                maxCharacters: editingConfig.maxCharacters ?? DEFAULT_MAX_CHARACTERS,
            });
            hydrate(editingConfig.pharmacyIds);
        } else {
            form.resetFields();
            form.setFieldsValue({ maxCharacters: DEFAULT_MAX_CHARACTERS });
            reset();
        }
    }, [open, editingConfig]);

    const handleFinish = async (values) => {
        if (!selectedPharmacies.length) {
            message.error('At least one pharmacy must be added');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                pharmacyName: values.pharmacyName,
                pharmacyIds: selectedPharmacies.map(p => p.pharmacyId),
                noteTemplate: values.noteTemplate,
                maxCharacters: values.maxCharacters,
            };

            if (editingConfig) {
                await API.update(editingConfig._id, payload);
                message.success('Config updated');
            } else {
                await API.create(payload);
                message.success('Config created');
            }

            onSuccess();
        } catch (err) {
            message.error(err?.response?.data?.message || 'An error occurred');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={<ModalTitle>{editingConfig ? 'Edit Pharmacy Note Config' : 'Add Pharmacy Note Config'}</ModalTitle>}
            open={open}
            onCancel={onCancel}
            footer={null}
            destroyOnClose
            width={620}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>

                <Form.Item
                    label={<FieldLabel>Pharmacies</FieldLabel>}
                    required
                    extra={<HelpText>Search and add one or more pharmacies that share this note template.</HelpText>}
                >
                    <Spin spinning={loadingPharmacies}>
                        {selectedPharmacies.length > 0 && (
                            <PharmacyTagList>
                                {selectedPharmacies.map(p => (
                                    <PharmacyTag key={p.pharmacyId}>
                                        {p.name || p.pharmacyId}
                                        {p.stateAbbr && <PharmacyTagState>, {p.stateAbbr}</PharmacyTagState>}
                                        <PharmacyTagId>({p.pharmacyId})</PharmacyTagId>
                                        <PharmacyTagRemove onClick={() => handleRemovePharmacy(p.pharmacyId)}>
                                            <CloseOutlined style={{ fontSize: 10 }} />
                                        </PharmacyTagRemove>
                                    </PharmacyTag>
                                ))}
                            </PharmacyTagList>
                        )}
                        <FullWidthAutoComplete
                            options={pharmacyOptions}
                            onSearch={handleSearch}
                            onSelect={handleSelectPharmacy}
                            value={searchValue}
                            onChange={setSearchValue}
                            loading={pharmaciesLoading}
                            placeholder="Search pharmacy name to add..."
                            notFoundContent={pharmaciesLoading ? 'Searching...' : null}
                        />
                    </Spin>
                </Form.Item>

                <Form.Item
                    label={<FieldLabel>Config Label</FieldLabel>}
                    name="pharmacyName"
                    rules={[{ required: true, message: 'A label is required' }]}
                    extra={<HelpText>Human-readable name for this config group. Auto-filled from the first pharmacy selected.</HelpText>}
                >
                    <Input placeholder="e.g. Curexa" />
                </Form.Item>

                <Form.Item
                    label={
                        <Space size={6}>
                            <FieldLabel>Note Template</FieldLabel>
                            <Tooltip title={<PlaceholderHelp />} overlayStyle={{ maxWidth: 460 }}>
                                <TooltipIcon />
                            </Tooltip>
                        </Space>
                    }
                    name="noteTemplate"
                    rules={[{ required: true, message: 'Note template is required' }]}
                    extra={<HelpText>Use placeholders to build the final note. Must include <code>{'{{pharmacyNotes}}'}</code>. The composed note (with placeholders resolved) must not exceed the max character limit.</HelpText>}
                >
                    <TextArea
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        placeholder="{{pharmacyNotes}} - Brand: {{clientName}}, LTC {{date}}"
                    />
                </Form.Item>

                <Form.Item
                    label={<FieldLabel>Max Characters</FieldLabel>}
                    name="maxCharacters"
                    rules={[{ required: true, message: 'Max characters is required' }]}
                    extra={<HelpText>The back end will reject prescriptions whose final composed note exceeds this limit.</HelpText>}
                >
                    <MaxCharsInput min={1} max={1000} />
                </Form.Item>

                <ModalFooter>
                    <Button onClick={onCancel}>Cancel</Button>
                    <SaveButton htmlType="submit" loading={saving}>
                        {editingConfig ? 'Save Changes' : 'Create'}
                    </SaveButton>
                </ModalFooter>
            </Form>
        </Modal>
    );
};
